import { format, parseISO, isValid } from 'date-fns'
import { ru } from 'date-fns/locale'

/**
 * Format ISO date string → DD.MM.YYYY HH:mm
 */
export function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return '—'
  try {
    const date = parseISO(dateString)
    if (!isValid(date)) return '—'
    return format(date, 'dd.MM.yyyy HH:mm', { locale: ru })
  } catch {
    return '—'
  }
}

/**
 * Format ISO date string → DD.MM.YYYY
 */
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '—'
  try {
    const date = parseISO(dateString)
    if (!isValid(date)) return '—'
    return format(date, 'dd.MM.yyyy', { locale: ru })
  } catch {
    return '—'
  }
}

/**
 * Normalize any date string to DD.MM.YYYY for display.
 * Accepts both DD.MM.YYYY and YYYY-MM-DD.
 */
export function normalizeDate(dateStr: string | null | undefined): string {
  if (!dateStr) return ''
  // Already DD.MM.YYYY
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) return dateStr
  // YYYY-MM-DD → DD.MM.YYYY
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-')
    return `${day}.${month}.${year}`
  }
  return dateStr
}

/**
 * Plural forms for Russian: кодов / код / кода
 */
export function pluralRu(
  count: number,
  one: string,
  few: string,
  many: string,
): string {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod100 >= 11 && mod100 <= 19) return `${count} ${many}`
  if (mod10 === 1) return `${count} ${one}`
  if (mod10 >= 2 && mod10 <= 4) return `${count} ${few}`
  return `${count} ${many}`
}

/**
 * Format any date string → YYYY-MM-DD for <input type="date">
 * Handles: YYYY-MM-DD, DD.MM.YYYY, ISO with T, datetime with space
 */
export function formatDateForInput(dateStr: string | undefined | null): string {
  if (!dateStr) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split('.')
    return `${year}-${month}-${day}`
  }
  if (dateStr.includes('T')) return dateStr.split('T')[0]
  if (dateStr.includes(' ')) return dateStr.split(' ')[0]
  return dateStr
}

/**
 * Convert YYYY-MM-DD → DD.MM.YYYY for API calls
 */
export function convertToApiFormat(date: string): string {
  if (!date) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-')
    return `${day}.${month}.${year}`
  }
  return date
}

/**
 * Mask raw digits into DD.MM.YYYY while typing
 */
export function maskDateInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8)
  if (digits.length >= 5) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`
  }
  if (digits.length >= 3) {
    return `${digits.slice(0, 2)}.${digits.slice(2)}`
  }
  return digits
}
